/**
 * Unified Device Detection Utility
 * Parses user agent ONCE and caches results for the session
 */

interface DeviceInfo {
  type: 'desktop' | 'mobile' | 'tablet';
  browser: string;
  os: string;
  language: string;
  timezone: string;
  viewport: { width: number; height: number };
  colorDepth: number;
  hardwareConcurrency: number;
  deviceMemory: number | null;
}

let cachedDeviceInfo: DeviceInfo | null = null;
let cachedDeviceHash: string | null = null;

/**
 * Parse user agent to extract device, browser, and OS info
 * Results are cached for the session
 */
export const getDeviceInfo = (): DeviceInfo => {
  if (cachedDeviceInfo) return cachedDeviceInfo;

  const ua = navigator.userAgent;

  // Device type detection
  let type: 'desktop' | 'mobile' | 'tablet' = 'desktop';
  if (/tablet|ipad/i.test(ua)) {
    type = 'tablet';
  } else if (/mobile|android|iphone/i.test(ua)) {
    type = 'mobile';
  }

  // Browser detection (order matters for accuracy)
  let browser = 'Unknown';
  if (/Edge|Edg/i.test(ua)) {
    browser = 'Edge';
  } else if (/Chrome/i.test(ua) && !/Chromium/i.test(ua)) {
    browser = 'Chrome';
  } else if (/Firefox/i.test(ua)) {
    browser = 'Firefox';
  } else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) {
    browser = 'Safari';
  } else if (/Opera|OPR/i.test(ua)) {
    browser = 'Opera';
  }

  // OS detection
  let os = 'Unknown';
  if (/Windows/i.test(ua)) {
    os = 'Windows';
  } else if (/Mac OS|MacOS/i.test(ua)) {
    os = 'macOS';
  } else if (/Android/i.test(ua)) {
    os = 'Android';
  } else if (/iOS|iPhone|iPad/i.test(ua)) {
    os = 'iOS';
  } else if (/Linux/i.test(ua)) {
    os = 'Linux';
  }

  cachedDeviceInfo = {
    type,
    browser,
    os,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    colorDepth: screen.colorDepth,
    hardwareConcurrency: navigator.hardwareConcurrency || 1,
    deviceMemory: (navigator as any).deviceMemory || null,
  };

  return cachedDeviceInfo;
};

/**
 * Generate a device fingerprint hash
 * Uses browser characteristics to create a semi-unique identifier
 */
const generateDeviceHash = (): string => {
  const components: string[] = [];

  // Browser info
  components.push(navigator.userAgent);
  components.push(navigator.language);
  components.push(screen.colorDepth.toString());
  components.push(screen.width.toString());
  components.push(screen.height.toString());
  components.push(new Date().getTimezoneOffset().toString());

  // Hardware concurrency
  if (navigator.hardwareConcurrency) {
    components.push(navigator.hardwareConcurrency.toString());
  }

  // Device memory
  if ('deviceMemory' in navigator) {
    components.push((navigator as any).deviceMemory.toString());
  }

  // Canvas fingerprint (lightweight)
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('ekselencia', 2, 2);
      components.push(canvas.toDataURL().substring(0, 50));
    }
  } catch {
    // Canvas fingerprinting blocked
  }

  // Create hash
  return hashString(components.join('|'));
};

/**
 * Simple string hash function (fast, 32-bit)
 */
const hashString = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
};

/**
 * Get or create a persistent device hash
 * Stored in localStorage for cross-session identification
 */
export const getDeviceHash = (): string => {
  if (cachedDeviceHash) return cachedDeviceHash;

  const storageKey = 'ekselencia_device_hash';

  // localStorage can be blocked (private mode / strict settings). In that case,
  // we still return a deterministic hash for the current runtime.
  try {
    const stored = localStorage.getItem(storageKey);

    if (stored) {
      cachedDeviceHash = stored;
      return stored;
    }

    const hash = generateDeviceHash();
    localStorage.setItem(storageKey, hash);
    cachedDeviceHash = hash;
    return hash;
  } catch {
    const hash = generateDeviceHash();
    cachedDeviceHash = hash;
    return hash;
  }
};

/**
 * Get or create a session ID
 * Refreshes after 30 minutes of inactivity
 */
/**
 * Get or create a visitor ID
 * Uses localStorage for persistence across sessions
 * Combines with daily rotation for privacy while maintaining accuracy
 */
export const getSessionId = (): string => {
  const visitorKey = 'ekselencia_visitor_id';
  const dateKey = 'ekselencia_visitor_date';
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  // Ensure the visitor id is stable (no random components) so multiple tracking
  // layers reconsolidate to one visitor/day.
  try {
    const storedId = localStorage.getItem(visitorKey);
    const storedDate = localStorage.getItem(dateKey);

    // Return existing ID if from today (same visitor, same day)
    if (storedId && storedDate === today) {
      return storedId;
    }

    const deviceHash = getDeviceHash();
    // Keep the same convention used by PresenceTracker
    const dailyId = `cli-${deviceHash}`;

    localStorage.setItem(visitorKey, dailyId);
    localStorage.setItem(dateKey, today);

    return dailyId;
  } catch {
    // Fallback (no storage): stable for this runtime
    const deviceHash = getDeviceHash();
    return `cli-${deviceHash}`;
  }
};

/**
 * Update cached viewport dimensions (call on resize)
 */
export const updateViewport = (): void => {
  if (cachedDeviceInfo) {
    cachedDeviceInfo.viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }
};

/**
 * Get UTM parameters from URL
 */
export const getUTMParams = (): {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
} => {
  const params = new URLSearchParams(window.location.search);
  return {
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
    utmTerm: params.get('utm_term') || undefined,
    utmContent: params.get('utm_content') || undefined,
  };
};

// Re-export for backward compatibility
export const getOrCreateDeviceHash = getDeviceHash;
export const getDeviceFingerprint = getDeviceHash;
